import type { ReactNode } from "react";
import { Link } from "react-router";
import { Icon } from "../../components/ui/Icon";
import { hideProvider } from "../../lib/connectionsApi";
import type { CampaignAction, LaunchView } from "../../lib/readyApi";
import { dayFullMonth, utcMs } from "../goal/text";
import { useCan } from "../../context/AuthContext";

export const OPEN = ["proposed", "approved", "executing"];

/** A time from the API in the owner's own clock: "3:40 pm", or "2 Oct, 3:40 pm" on another day. */
export function clockTime(s: string | null | undefined): string {
  const ms = utcMs(s);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const t = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
  return d.toDateString() === new Date().toDateString() ? t : `${d.getDate()} ${d.toLocaleDateString("en-US", { month: "short" })}, ${t}`;
}

/** What a campaign action that did not go through says, in plain words. */
export const notDone = (a: CampaignAction | undefined, doing: string) =>
  !a ? null : a.state === "failed" ? hideProvider(`${doing} did not work. ${a.result?.error ?? ""}`.trim()) : a.state === "refused" ? hideProvider(a.reason ? `${doing} was held back: ${a.reason}` : `${doing} was held back by the goal's rules.`) : null;

/** Where Launch stands: the campaign's latest actions, whether one is under way, and why it still waits. */
export function launchState(launch: LaunchView) {
  const latest = (kind: string) => launch.actions.find((a) => a.kind === kind);
  const create = latest("campaign.create");
  const turnOn = latest("campaign.turn_on");
  const campaign = launch.campaign;
  const creating = (create && OPEN.includes(create.state)) || campaign?.state === "creating";
  const switching = !!turnOn && OPEN.includes(turnOn.state);
  const failing = launch.checks.filter((c) => !c.ok && !c.optional);
  const adsCheck = failing.find((c) => c.id === "ads");
  const wait = adsCheck?.detail ?? failing[0]?.detail ?? (failing[0] ? failing[0].label : null);
  const pending = launch.actions.some((a) => OPEN.includes(a.state)) || campaign?.state === "creating";
  return { create, turnOn, campaign, creating, switching, wait, pending };
}

/**
 * The bottom bar of a goal's ads before and after Launch: the reason Launch waits just above it, then Launch
 * (with the daily budget beside it on Ads); once launched, whether the ads run.
 */
export function LaunchFoot({
  goalId,
  launch,
  launchedAt,
  busy,
  budget,
  onLaunch,
  onTurnOn,
}: {
  goalId: string;
  launch: LaunchView;
  launchedAt: string | null;
  busy: boolean;
  /** The daily budget's button, beside Launch. */
  budget?: ReactNode;
  onLaunch: () => void;
  onTurnOn: () => void;
}) {
  const may = useCan()("admin");
  const s = launchState(launch);
  const c = s.campaign;
  if (c?.state === "on")
    return (
      <div className="pn-foot ad-lf">
        <div className="ad-lb">
          <span className="ad-live" aria-hidden="true" />
          <span className="ad-lbt">
            <b>Running</b>
            {Number.isFinite(utcMs(launchedAt)) && <span>Since {dayFullMonth(new Date(utcMs(launchedAt)))}</span>}
          </span>
          <Link className="pn-btn sm" to={`/goals/${goalId}`}>
            Results
          </Link>
        </div>
      </div>
    );
  if (s.creating || c?.state === "unsure")
    return (
      <div className="pn-foot ad-lf">
        <div className="ad-lb">
          <span className="ad-spin" aria-hidden="true" />
          <span className="ad-lbt">
            <b>{s.creating ? "Sending your ads to Meta" : "Meta gave an unclear answer"}</b>
            {!s.creating && <span>Flashadz looks again at {clockTime(c?.settleUntil)}, before making anything new.</span>}
          </span>
        </div>
      </div>
    );
  if (c)
    return (
      <div className="pn-foot ad-lf">
        <div className="ad-lb">
          {s.switching ? <span className="ad-spin" aria-hidden="true" /> : <span className="ad-live off" aria-hidden="true" />}
          <span className="ad-lbt">
            <b>{s.switching ? "Switching the ads on" : "Paused"}</b>
            {!s.switching && <span>{notDone(s.turnOn, "Switching the ads on") ?? "On Meta, not spending"}</span>}
          </span>
          {!s.switching && may && (
            <button className="pn-btn pri sm" type="button" disabled={busy} onClick={onTurnOn}>
              {s.turnOn ? "Try again" : "Turn it on"}
            </button>
          )}
        </div>
      </div>
    );
  const failed = notDone(s.create, "Launching");
  const off = !!s.wait || busy || s.pending;
  return (
    <div className="pn-foot ad-lf">
      {failed && <p className="ad-why bad">{failed}</p>}
      {s.wait && (
        <p className="ad-why">
          <Icon name="clock" />
          <span>{s.wait}</span>
        </p>
      )}
      <div className="ad-lb">
        {budget}
        {may && (
          <button className="pn-btn pri ad-go" type="button" disabled={off} onClick={onLaunch}>
            <Icon name="check" />
            Launch
          </button>
        )}
      </div>
    </div>
  );
}
